import {
  MagnifyingGlass,
  List,
  CaretDown,
  User,
  DeviceMobile,
  Bell,
  Scan,
  SignOut,
} from '@phosphor-icons/react';
import { Warehouse } from 'lucide-react';
import { GlobalSearchInput } from './GlobalSearchInput';
import { KnowledgeHelp } from './KnowledgeHelp';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { QuickPrintButton } from '@/components/print-labels/QuickPrintButton';
import { EnvSwitcher } from './EnvSwitcher';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import {
  useWarehouseStore,
  type WarehouseOption,
} from '@/stores/warehouse-store';
import { useChannelStore } from '@/stores/channel-store';
import { useChannels, useAccessibleChannels } from '@/api/hooks/useChannels';
import {
  resolveChannelList,
  reconcileChannelSelection,
  showAllChannelsOption,
} from './channel-reconcile';
import { useAuthStore } from '@/stores/auth-store';
import { useAuth } from '@/providers/AuthProvider';
import { usePageTitleValue } from '@/contexts/page-title-context';
import { cn } from '@/lib/utils';
import {
  getHeaderTintClass,
  getChannelTintClass,
  getChannelPillClass,
  getChannelDotClass,
} from '@/lib/environment';

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function Header({ onMenuClick, showMenuButton = false }: HeaderProps) {
  const {
    accessibleWarehouses,
    warehouses,
    selectedWarehouseId,
    isAllMode,
    setSelectedWarehouse,
    setAllMode,
  } = useWarehouseStore();
  const selectedChannel = useChannelStore((s) => s.selectedChannel);
  const setChannel = useChannelStore((s) => s.setChannel);
  const { data: channels } = useChannels();
  // The user's accessible channels + default lens at the active warehouse.
  // Disabled (undefined) until a warehouse is known; restricted users see only
  // `access.channels`, unrestricted users keep the full `channels` list.
  const { data: access } = useAccessibleChannels(
    selectedWarehouseId ?? undefined,
  );
  const channelList = resolveChannelList(access, channels);
  const { user } = useAuthStore();
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const { signOut } = useAuth();
  const pageTitle = usePageTitleValue();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Resolve the persisted slug to its channel row for the label + accent.
  const activeChannel = channelList?.find((c) => c.slug === selectedChannel);
  const channelLabel =
    selectedChannel === 'ALL'
      ? 'All'
      : (activeChannel?.name ?? selectedChannel);
  const channelColor = activeChannel?.color;

  // Channel-selection reconciliation. Composes v4 restricted-user access (seed
  // the per-warehouse default lens; coerce an out-of-set slug to the default)
  // with the v3 selected-channel-deleted fallback (a persisted slug matching no
  // active channel reverts to ALL). Guards on `access`/`channels` being defined
  // so a transient fetch failure never clears a valid selection. Re-runs on a
  // warehouse switch because `access` refetches for the new warehouse.
  useEffect(() => {
    const next = reconcileChannelSelection(access, channels, selectedChannel);
    if (next !== null && next !== selectedChannel) {
      setChannel(next);
    }
  }, [access, channels, selectedChannel, setChannel]);

  // Admins see all warehouses; non-admins see only their assignments
  const allWarehouseOptions: WarehouseOption[] = warehouses.map((w) => ({
    warehouseId: w.id,
    warehouseCode: w.code,
    warehouseName: w.name,
    isPrimary: false,
    roles: [],
  }));
  const displayWarehouses = isAdmin
    ? allWarehouseOptions
    : accessibleWarehouses.length > 0
      ? accessibleWarehouses
      : allWarehouseOptions;

  const handleWarehouseChange = (value: string) => {
    if (value === '__all__') {
      setAllMode(true);
    } else {
      setAllMode(false);
      setSelectedWarehouse(value);
    }
  };

  const currentValue = isAllMode ? '__all__' : selectedWarehouseId || '';

  // Show the switcher whenever there's a warehouse context — a loaded list, a
  // persisted selection, or All mode — so a transient list-load failure can't
  // make the only switcher entry point vanish.
  const showSwitcher =
    displayWarehouses.length > 0 || !!selectedWarehouseId || isAllMode;

  // Display name + code for the active warehouse
  const activeWarehouse = displayWarehouses.find(
    (w) => w.warehouseId === selectedWarehouseId,
  );
  const activeWarehouseName = isAllMode
    ? 'All Warehouses'
    : activeWarehouse?.warehouseName || 'Select warehouse';
  const activeWarehouseCode = isAllMode
    ? 'ALL'
    : activeWarehouse?.warehouseCode || '';

  // Radio items for the warehouse switcher dropdown in the header
  const warehouseRadioItems = (
    <>
      {isAdmin && (
        <DropdownMenuRadioItem value="__all__">
          All Warehouses
        </DropdownMenuRadioItem>
      )}
      {displayWarehouses.map((wh) => (
        <DropdownMenuRadioItem key={wh.warehouseId} value={wh.warehouseId}>
          {wh.warehouseName} ({wh.warehouseCode})
        </DropdownMenuRadioItem>
      ))}
    </>
  );

  const userInitials = user?.name
    ? user.name
        .split(' ')
        .filter(Boolean)
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : 'U';

  return (
    <header
      data-testid="header"
      className={cn(
        'sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        // Color-code the bar by environment (local=amber, dev=green) and, in
        // prod, by the active warehouse — so it's never mistaken for another.
        getHeaderTintClass(activeWarehouseCode),
        // Channel reads as a bottom-border accent so it composes with the
        // warehouse background tint instead of competing with it.
        getChannelTintClass(channelColor),
      )}
    >
      <div className="flex h-14 lg:h-16 items-center gap-2 lg:gap-4 px-4 lg:px-6">
        {/* Mobile menu button */}
        {showMenuButton && (
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onMenuClick}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <List size={20} weight="bold" />
          </Button>
        )}

        {/* Search - hidden on mobile, visible on tablet+ */}
        <div className="hidden sm:flex flex-1 max-w-md">
          <GlobalSearchInput />
        </div>

        {/* Mobile page title */}
        {pageTitle && (
          <span className="lg:hidden min-w-0 text-base font-semibold truncate">
            {pageTitle}
          </span>
        )}

        {/* Spacer to push right items */}
        <div className="flex-1" />

        {/* Right side actions. `min-w-0` lets this cluster shrink instead of
            pushing the avatar past the right edge on a phone. */}
        <div className="flex min-w-0 items-center gap-1 lg:gap-2">
          {/* Secondary controls scroll sideways on a phone rather than
              overflowing off-screen; the avatar stays pinned (below) so it's
              never the item that gets clipped. `py-1.5 -my-1.5` reserves room
              for the notification badge, which overhangs its button and would
              otherwise be clipped by the scroll container. */}
          <div
            className="flex min-w-0 items-center gap-1 overflow-x-auto scrollbar-subtle py-1.5 -my-1.5 lg:gap-2 lg:overflow-x-visible"
            data-testid="header-actions-scroll"
          >
            {/* Mobile search icon */}
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 sm:hidden"
              aria-label="Search"
              onClick={() => setMobileSearchOpen((v) => !v)}
              data-testid="mobile-search-btn"
            >
              <MagnifyingGlass size={20} />
            </Button>

            {/*
              Scan — the floor tool, one tap from anywhere.

              `lg:hidden` because that is where the persistent sidebar (which
              carries the same entry) disappears behind the menu button. It is
              NOT in mobile-nav.tsx: six labelled items plus the ⋮ menu button
              already share the width of a 360px phone there and truncate their
              labels to survive it, so another labelled item would break the
              row for everyone.
            */}
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="shrink-0 lg:hidden"
              data-testid="mobile-scan-btn"
            >
              <Link to="/scan" aria-label="Scan">
                <Scan size={20} />
              </Link>
            </Button>

            {/* Internal env switcher (gated) — jump local/dev/prod, keep the
                path. `shrink-0` goes on the components themselves rather than
                a wrapper: EnvSwitcher renders null for everyone outside the
                allowlist, and a wrapper would still claim a gap slot. */}
            <EnvSwitcher className="shrink-0" />

            {/* Quick print */}
            <QuickPrintButton className="shrink-0" />

            {/* Notifications */}
            <NotificationBell className="shrink-0" />

            {/* Warehouse switcher — the code tag IS the dropdown trigger.
                2 chars on mobile, full code on desktop. */}
            {showSwitcher && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 shrink-0 gap-1 px-2 font-mono text-xs"
                    title={activeWarehouseName}
                    data-testid="warehouse-switcher"
                  >
                    {/* Icon on desktop only; mobile shows just code + caret */}
                    <Warehouse size={15} className="hidden lg:block" />
                    <span className="lg:hidden">
                      {activeWarehouseCode
                        ? activeWarehouseCode.slice(0, 2)
                        : '—'}
                    </span>
                    <span className="hidden lg:inline">
                      {activeWarehouseCode || 'Warehouse'}
                    </span>
                    <CaretDown size={12} className="opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs">
                    Switch warehouse
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup
                    value={currentValue}
                    onValueChange={handleWarehouseChange}
                  >
                    {warehouseRadioItems}
                  </DropdownMenuRadioGroup>
                  {displayWarehouses.length === 0 && (
                    <DropdownMenuItem
                      disabled
                      className="text-xs text-muted-foreground"
                    >
                      Loading warehouses…
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Business-channel switcher — sibling to the warehouse selector.
                Trigger shows the active channel with a colored accent pill. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  size="sm"
                  className={cn(
                    'h-8 shrink-0 gap-1 px-2 text-xs',
                    getChannelPillClass(channelColor),
                  )}
                  title={`Channel: ${channelLabel}`}
                  data-testid="channel-switcher"
                >
                  <span>{channelLabel}</span>
                  <CaretDown size={12} className="opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs">
                  Business channel
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                  value={selectedChannel}
                  onValueChange={(value) => setChannel(value)}
                >
                  {showAllChannelsOption(access) && (
                    <DropdownMenuRadioItem value="ALL">
                      All
                    </DropdownMenuRadioItem>
                  )}
                  {channelList?.map((ch) => (
                    <DropdownMenuRadioItem
                      key={ch.id}
                      value={ch.slug}
                      title={ch.description ?? ''}
                    >
                      <span
                        className={cn(
                          'mr-2 inline-block h-2.5 w-2.5 rounded-full',
                          getChannelDotClass(ch.color),
                        )}
                        aria-hidden="true"
                      />
                      {ch.name}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <KnowledgeHelp />

          {/* User menu with warehouse selector inside. Pinned outside the
              scroller so the avatar is always reachable without swiping. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="shrink-0 gap-2 px-2 lg:px-3"
                data-testid="user-menu-btn"
              >
                <div className="h-8 w-8 shrink-0 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-foreground">
                    {userInitials}
                  </span>
                </div>
                <span className="hidden lg:block text-sm font-medium">
                  {user?.name || 'User'}
                </span>
                <CaretDown
                  size={14}
                  className="hidden lg:block text-muted-foreground"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.name || 'User'}</p>
                  <p className="text-xs text-muted-foreground">
                    {user?.email || ''}
                  </p>
                  <Badge variant="secondary" className="w-fit text-xs">
                    {user?.isAdmin
                      ? 'ADMIN'
                      : user?.isAuditor
                        ? 'AUDITOR'
                        : 'USER'}
                  </Badge>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center">
                  <User size={16} className="mr-2" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/device-settings" className="flex items-center">
                  <DeviceMobile size={16} className="mr-2" />
                  Device Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings/alerts" className="flex items-center">
                  <Bell size={16} className="mr-2" />
                  Alert Preferences
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => signOut()}
                className="text-destructive"
                data-testid="logout-btn"
              >
                <SignOut size={16} className="mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/* Mobile search bar — shown when mobile search icon is clicked.
          Stays on the default surface (it sits below the bar, so it must not
          inherit the env/warehouse tint). */}
      {mobileSearchOpen && (
        <div className="sm:hidden bg-background px-4 pb-3 pt-2 border-t border-border">
          <GlobalSearchInput />
        </div>
      )}
    </header>
  );
}
